import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} Refs
 * @property {HTMLScriptElement} [map]
 * @property {HTMLElement} content
 */

/**
 * Swaps product description HTML when the selected variant changes.
 * Descriptions come from variant metafield wumboo.description (JSON map in Liquid).
 *
 * @extends {Component<Refs>}
 */
class WumbooVariantDescription extends Component {
  /** @type {Record<string, string>} */
  #map = {};

  connectedCallback() {
    super.connectedCallback();
    this.#loadMap();
    this.#apply(this.refs.content?.dataset?.variantId);

    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#controller.signal,
      capture: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #controller = new AbortController();

  #loadMap() {
    const script = this.refs.map;
    if (!(script instanceof HTMLScriptElement)) return;
    try {
      this.#map = JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('[wumboo-variant-description] Failed to parse description map', error);
      this.#map = {};
    }
  }

  /**
   * @param {string | number | undefined | null} variantId
   */
  #apply(variantId) {
    const { content } = this.refs;
    if (!content || variantId == null || variantId === '') return;
    const key = String(variantId).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    const html = this.#map[key] ?? this.#map[String(variantId)];
    if (typeof html !== 'string') return;
    content.innerHTML = html;
    content.dataset.variantId = key;
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const root = this.closest('.shopify-section, dialog');
    if (root && !root.contains(event.target)) return;
    if (!this.isConnected) return;

    // Prefer sync variant id from the event when available
    const syncId = event.detail?.variantId;
    if (syncId) this.#apply(syncId);

    event.promise
      .then(({ detail }) => {
        if (!this.isConnected) return;
        const id = detail?.resource?.id ?? detail?.variantId ?? syncId;
        if (id) {
          this.#apply(id);
          return;
        }
        // Fallback: pull updated node from section HTML (keeps map in sync)
        const html = detail?.html;
        if (!html) return;
        const next =
          html.querySelector(`wumboo-variant-description[data-block-id="${this.dataset.blockId}"]`) ||
          html.querySelector('wumboo-variant-description');
        if (!next) return;
        this.replaceWith(document.importNode(next, true));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[wumboo-variant-description] Event promise rejected:', error);
        }
      });
  };
}

if (!customElements.get('wumboo-variant-description')) {
  customElements.define('wumboo-variant-description', WumbooVariantDescription);
}
